import { useState, type ReactNode } from 'react';
import { Navigate, NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import { useMutation } from '@tanstack/react-query';
import { LogOut } from 'lucide-react';
import type { AdminMe, AdminSessionState, ThemePref } from '@hoopify/shared';
import { api, ApiError, errorMessage } from '../../lib/api';
import { useAdmin, useAdminSession, useSetAdminSession } from '../../lib/admin';
import { Alert, Button, Dialog, LoadingBlock, TextArea } from '../ui';
import { AdminBadge } from '../../pages/admin/AdminAuthPages';
import { Wordmark } from '../Wordmark';
import { ThemeProvider, ThemeSwitch } from '../ThemeSwitch';
import { useAccountTheme } from '../../lib/theme';

/** The admin console needs its own admin sign-in. Coach sign-in never counts. */
export function RequireAdmin() {
  const { data, isLoading } = useAdminSession();
  const location = useLocation();
  if (isLoading) return <LoadingBlock />;
  if (data?.status !== 'signed_in') {
    const next = location.pathname + location.search;
    // Coming back to a page after the session ended: say why.
    const wasIn = sessionStorageGet('hoopify.admin.was') === '1';
    return <Navigate to={`/admin/login?next=${encodeURIComponent(next)}${wasIn ? '&expired=1' : ''}`} replace />;
  }
  sessionStorageSet('hoopify.admin.was', '1');
  return <Outlet />;
}

function sessionStorageGet(k: string) {
  try {
    return sessionStorage.getItem(k);
  } catch {
    return null;
  }
}
function sessionStorageSet(k: string, v: string | null) {
  try {
    if (v === null) sessionStorage.removeItem(k);
    else sessionStorage.setItem(k, v);
  } catch {
    /* ignore */
  }
}

const NAV = [
  { to: '/admin', label: 'Overview', end: true },
  { to: '/admin/accounts', label: 'Accounts' },
  { to: '/admin/teams', label: 'Teams' },
  { to: '/admin/clubs', label: 'Clubs' },
  { to: '/admin/email', label: 'Email' },
  { to: '/admin/server', label: 'Server' },
  { to: '/admin/log', label: 'Admin log' },
  { to: '/admin/admins', label: 'Admins' },
];

export function AdminLayout() {
  const admin = useAdmin();
  const setSession = useSetAdminSession();
  const navigate = useNavigate();
  const logout = useMutation({
    mutationFn: () => api.post<AdminSessionState>('/admin/logout'),
    onSuccess: (s) => {
      sessionStorageSet('hoopify.admin.was', null);
      setSession(s);
      navigate('/admin/login');
    },
  });

  const theme = useAccountTheme(admin.theme, async (t: ThemePref) => setSession({ status: 'signed_in', admin: await api.put<AdminMe>('/admin/me/theme', { theme: t }) }));

  return (
    <ThemeProvider value={theme}>
    <div className="min-h-dvh">
      <header data-theme="dark" className="sticky top-0 z-30 border-b border-white/10 bg-[#0b0b14] text-white [color-scheme:dark]">
        <div className="mx-auto flex h-14 max-w-[1240px] items-center gap-3 px-4 sm:px-8">
          <span className="flex items-center gap-3">
            <Wordmark size={20} /> <AdminBadge onDark />
          </span>
          <nav className="ml-6 hidden gap-1 md:flex">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.end} className={({ isActive }) => `rounded-md px-3 py-1.5 text-[14px] font-medium ${isActive ? 'bg-white/15 text-white' : 'text-white/70 hover:text-white'}`}>
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="ml-auto flex min-w-0 items-center gap-2">
            <ThemeSwitch compact />
            <span className="hidden truncate text-[14px] text-white/70 sm:inline">{admin.name}</span>
            <button className="rounded-md p-2 text-white/70 hover:bg-white/10 hover:text-white" onClick={() => logout.mutate()} aria-label="Sign out" title="Sign out">
              <LogOut className="size-4" aria-hidden />
            </button>
          </div>
        </div>
        <nav className="flex gap-1 overflow-x-auto px-3 pb-2 md:hidden">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} className={({ isActive }) => `shrink-0 rounded-md px-3 py-1.5 text-[14px] font-medium ${isActive ? 'bg-white/15 text-white' : 'text-white/70'}`}>
              {n.label}
            </NavLink>
          ))}
        </nav>
      </header>
      <main className="mx-auto max-w-[1240px] px-4 py-6 sm:px-8 sm:py-8">
        <Outlet />
      </main>
    </div>
    </ThemeProvider>
  );
}

/** Admin actions that affect a coach: ask why, keep it in the log. */
export function ReasonDialog({
  open,
  onClose,
  title,
  children,
  confirm,
  tone = 'primary',
  run,
  extra,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children?: ReactNode;
  confirm: string;
  tone?: 'primary' | 'danger';
  run: (reason: string) => Promise<unknown>;
  extra?: ReactNode;
}) {
  const [reason, setReason] = useState('');
  const act = useMutation({
    mutationFn: () => run(reason),
    onSuccess: () => {
      setReason('');
      onClose();
    },
  });
  const close = () => {
    act.reset();
    setReason('');
    onClose();
  };
  const err = act.error instanceof ApiError ? act.error.fields : {};
  return (
    <Dialog open={open} onClose={close} title={title}>
      <form onSubmit={(e) => { e.preventDefault(); act.mutate(); }} className="space-y-4" noValidate>
        {children && <div className="text-muted">{children}</div>}
        {act.isError && !err.reason && <Alert>{errorMessage(act.error)}</Alert>}
        {extra}
        <TextArea label="Reason" hint="Kept in the admin log." value={reason} onChange={(e) => setReason(e.target.value)} error={err.reason} rows={2} required />
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={close}>
            Cancel
          </Button>
          <Button type="submit" variant={tone} loading={act.isPending} disabled={reason.trim().length < 3}>
            {confirm}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

export function Stat({ label, value, sub }: { label: string; value: ReactNode; sub?: ReactNode }) {
  return (
    <div className="rounded-lg border border-line bg-surface px-4 py-3">
      <p className="label-caps text-muted">{label}</p>
      <p className="tnum font-display mt-1 text-[28px] leading-tight font-black">{value}</p>
      {sub && <p className="tnum mt-0.5 text-[13px] text-muted">{sub}</p>}
    </div>
  );
}

export function Pager({ page, total, pageSize, onPage }: { page: number; total: number; pageSize: number; onPage: (p: number) => void }) {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  if (pages <= 1) return null;
  return (
    <div className="mt-4 flex items-center justify-between text-[14px] text-muted">
      <span className="tnum">
        Page {page} of {pages}
      </span>
      <div className="flex gap-2">
        <Button variant="secondary" disabled={page <= 1} onClick={() => onPage(page - 1)}>
          Previous
        </Button>
        <Button variant="secondary" disabled={page >= pages} onClick={() => onPage(page + 1)}>
          Next
        </Button>
      </div>
    </div>
  );
}
